<script setup>
  import { computed, onUnmounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useOfferStore } from '../../stores/OfferStore';
  import { useBrowseCartStore } from '../../stores/BrowseCartStore';
  import { summaryKeys, lineAmount, CHILDREN_AUDIENCES } from '../../utils/cart_summary.js';
  import { showPriceWithCurrency } from '../../utils/format_offer.js';

  const { t, te } = useI18n();
  const offerStore = useOfferStore();
  const cartStore = useBrowseCartStore();
  const collapsed = ref(false);

  const LICENSE_MODELS = ['limited_loans', 'limited_duration', 'concurrent_access', 'perpetual'];
  const AUDIENCES = ['general', 'adult', 'teen', 'young_adult', 'children', ...CHILDREN_AUDIENCES];
  const CATEGORIES = ['fiction', 'non_fiction'];
  // FBKS-3962: "À revoir" is pending until cart items can be flagged for review.
  const REVIEWS = ['unavailable', 'already_purchased', 'in_another_cart'];

  const currency = computed(() => cartStore.cart.financial?.billing_currency);
  // Deleted offers are only returned for the removal notice, their rows are hidden
  const offers = computed(() => offerStore.offers.filter(item => !item.only_offer_deleted));

  const buckets = computed(() => {
    const result = { license_model: {}, format: {}, audience: {}, category: {}, language: {}, review: {} };
    const add = (group, key, amount) => {
      if (!key) return;
      const bucket = result[group][key] ||= { count: 0, amount: 0 };
      bucket.count += 1;
      bucket.amount += amount;
    };

    offers.value.forEach(item => {
      const keys = summaryKeys(item);
      const amount = lineAmount(item, cartStore.cart.financial?.exchange_rates);
      ['license_model', 'format', 'audience', 'category', 'language'].forEach(group => add(group, keys[group], amount));
      if (CHILDREN_AUDIENCES.includes(keys.audience)) add('audience', 'children', amount);
      keys.review.forEach(key => add('review', key, amount));
    });
    return result;
  });

  const byCount = (group) => Object.keys(buckets.value[group]).sort((a, b) => buckets.value[group][b].count - buckets.value[group][a].count);

  const translateOr = (key, fallback) => te(key) ? t(key) : fallback.toUpperCase();

  // Selected values stay listed (even at zero) so their filter can always be cleared
  const rows = (group, keys, label) => {
    const selected = offerStore.summary_filters.filter(filter => filter.group === group).map(filter => filter.value);
    return [...new Set([...keys, ...selected])]
      .filter(key => buckets.value[group][key] || selected.includes(key))
      .map(key => ({ group, key, label: label(key), ...(buckets.value[group][key] || { count: 0, amount: 0 }) }));
  };

  const sections = computed(() => [
    [
      { title: t('cart.summary.licenses'), subtitle: t('cart.summary.license_model'), rows: rows('license_model', LICENSE_MODELS, key => t(`cart.summary.license_models.${key}`)) },
      { subtitle: t('cart.summary.format'), rows: rows('format', byCount('format'), key => translateOr(`book_format.name.${key}`, key)) }
    ],
    [
      { title: t('cart.header.labels.audience'), rows: rows('audience', AUDIENCES, key => t(`cart.audience.${key}`)) }
    ],
    [
      { title: t('cart.header.labels.category'), rows: rows('category', CATEGORIES, key => t(`cart.category.${key}`)) },
      { title: t('cart.summary.language'), rows: rows('language', byCount('language'), key => translateOr(`components.catalog_publication.table.language.${key}`, key)) }
    ],
    [
      { title: t('cart.summary.to_review'), counts_only: true, rows: rows('review', REVIEWS, key => t(`cart.summary.review.${key}`)) }
    ]
  ]);

  const isActive = (row) => offerStore.summary_filters.some(filter => filter.group === row.group && filter.value === row.key);
  const width = (row) => `${offers.value.length ? (row.count / offers.value.length) * 100 : 0}%`;

  // Summary filters live in the store, don't carry them over to another cart
  onUnmounted(() => { offerStore.summary_filters = []; });
</script>

<template>
  <section class="cart-summary border rounded-3 bg-light p-3 mt-3 mb-4" aria-labelledby="cart-summary-title">
    <div class="d-flex align-items-center gap-3">
      <h2 id="cart-summary-title" class="fs-5 mb-0">{{ t('cart.summary.title') }}</h2>
      <span class="text-muted small">{{ t('cart.summary.hint') }}</span>
      <button type="button" class="btn btn-outline-secondary btn-sm ms-auto" :aria-expanded="!collapsed" @click="collapsed = !collapsed">
        {{ collapsed ? t('cart.summary.expand') : t('cart.summary.collapse') }}
        <i class="bi" :class="collapsed ? 'bi-chevron-down' : 'bi-chevron-up'" aria-hidden="true"></i>
      </button>
    </div>

    <div class="row g-3 mt-1" v-if="!collapsed">
      <div class="col-12 col-md-6 col-xl-3" v-for="(column, index) in sections" :key="index">
        <div class="bg-white border rounded-3 p-3 h-100">
          <div v-for="section in column" :key="section.title || section.subtitle" class="mb-3">
            <div class="d-flex justify-content-between align-items-baseline">
              <h3 class="fs-6 fw-bold mb-1" v-if="section.title">{{ section.title }}</h3>
              <span class="text-muted small ms-auto" v-if="section.title">{{ section.counts_only ? t('cart.summary.lines') : t('cart.summary.lines_amount') }}</span>
            </div>
            <h4 class="text-uppercase text-muted small fw-semibold mt-2 mb-1" v-if="section.subtitle">{{ section.subtitle }}</h4>
            <button
              v-for="row in section.rows"
              :key="row.key"
              type="button"
              class="cart-summary__row btn btn-link text-reset text-decoration-none w-100 text-start px-2 py-1"
              :class="{ 'cart-summary__row--active': isActive(row), 'ps-4': CHILDREN_AUDIENCES.includes(row.key), 'fw-semibold': row.key === 'children' }"
              :aria-pressed="isActive(row)"
              @click="offerStore.toggleSummaryFilter(row.group, row.key)">
              <span class="d-flex gap-3">
                <span class="flex-grow-1">{{ row.label }}</span>
                <span class="fw-bold">{{ row.count }}</span>
                <span class="cart-summary__amount text-muted text-end" v-if="!section.counts_only">{{ showPriceWithCurrency(currency, row.amount) }}</span>
              </span>
              <span class="progress mt-1" role="presentation" v-if="!section.counts_only">
                <span class="progress-bar" :style="{ width: width(row) }"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
  .cart-summary__row {
    border-radius: 0.375rem;
  }
  .cart-summary__row:hover,
  .cart-summary__row--active {
    background-color: var(--bs-success-bg-subtle);
  }
  .cart-summary__amount {
    min-width: 7rem;
  }
  .cart-summary .progress {
    display: flex;
    height: 0.25rem;
  }
  .cart-summary .progress-bar {
    background-color: var(--primary);
  }
</style>
